/**
 * Staff Service
 * API calls for staff/HR management
 */

import { api } from './api.js';
import type { PaginatedResponse } from '../types/api.js';
import type {
  Staff,
  StaffCreateInput,
  StaffUpdateInput,
  StaffListParams,
  StaffStatistics,
  DepartmentInfo,
} from '../types/staff.js';

export const staffService = {
  async list(params?: StaffListParams): Promise<PaginatedResponse<Staff>> {
    const response = await api.get<PaginatedResponse<Staff>>('/staff', { params });
    return response.data;
  },

  async getById(staffId: string): Promise<Staff> {
    const response = await api.get<Staff>(`/staff/${staffId}`);
    return response.data;
  },

  async create(data: StaffCreateInput): Promise<Staff> {
    const response = await api.post<Staff>('/staff', data);
    return response.data;
  },

  async update(staffId: string, data: StaffUpdateInput): Promise<Staff> {
    const response = await api.put<Staff>(`/staff/${staffId}`, data);
    return response.data;
  },

  async delete(staffId: string): Promise<void> {
    await api.delete(`/staff/${staffId}`);
  },

  async bulkDelete(staffIds: string[]): Promise<{ deleted: number }> {
    const response = await api.post<{ deleted: number }>('/staff/bulk-delete', { ids: staffIds });
    return response.data;
  },

  async getStatistics(): Promise<StaffStatistics> {
    const response = await api.get<StaffStatistics>('/staff/statistics');
    return response.data;
  },

  async getDepartments(includeCount = false): Promise<DepartmentInfo[]> {
    const response = await api.get<DepartmentInfo[]>('/staff/departments', {
      params: { includeCount },
    });
    return response.data;
  },

  async getPositions(): Promise<string[]> {
    const response = await api.get<string[]>('/staff/positions');
    return response.data;
  },

  async exportStaff(options: {
    status?: string;
    department?: string;
    includeSalary?: boolean;
    format?: 'csv' | 'xlsx';
  }): Promise<Blob> {
    const response = await api.get<Blob>('/staff/export', {
      params: options,
      responseType: 'blob',
    });
    return response.data;
  },
};
